import { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { CheckCircleIcon, Image, PureButton, type SliderSlide } from '@jsmdg/yoshi';
import { type Product } from '../../../shared/types/searchResponse';
import { LazySlider } from '../LazySlider';
import { getPrimaryImage, trackSwitchMotif } from './helpers';
import { useSwiperSync } from './useSwiperSync';
import { thumbnailSliderConfig } from './valueVoucherSliderConfig';
import styles from './BestsellerValueVoucherSlider.module.scss';

type ThumbnailSliderProps = {
    readonly products: Product[];
    readonly activeSlideIndex: number;
    readonly onSlideChange: (index: number) => void;
    readonly a11yNextSlide: string;
    readonly a11yPreviousSlide: string;
};

const messages = defineMessages({
    thumbnailLabel: { defaultMessage: 'Wunschmotiv wählen' },
});

const ThumbnailSlider = ({
    a11yNextSlide,
    a11yPreviousSlide,
    activeSlideIndex,
    onSlideChange,
    products,
}: ThumbnailSliderProps): JSX.Element | null => {
    const intl = useIntl();
    const containerRef = useRef<HTMLDivElement>(null);
    const slideKeys = products.map(product => product.productId).join(',');
    const { onSwiperSlideChange } = useSwiperSync(containerRef, {
        activeSlideIndex,
        onSlideChange,
        slideKeys,
        slidesCount: products.length,
    });

    if (!products.length) {
        return null;
    }

    const handleThumbnailClick = (index: number): void => {
        if (index !== activeSlideIndex) {
            void trackSwitchMotif(
                'slide in thumbnail',
                index > activeSlideIndex ? 'slide next' : 'slide previous',
            );
        }

        onSlideChange(index);
    };

    const slides: SliderSlide[] = products.map((product, index) => {
        const image = getPrimaryImage(product);
        const isSelected = index === activeSlideIndex;

        return {
            key: `thumbnail-${product.productId}`,
            content: (
                <PureButton
                    aria-label={product.title}
                    aria-pressed={isSelected}
                    className={classNames(
                        'overflow-hidden position-relative w-100 p-0',
                        styles.thumbnailButton,
                        {
                            [styles.thumbnailButtonSelected]: isSelected,
                        },
                    )}
                    onClick={() => handleThumbnailClick(index)}
                >
                    <Image
                        alt={image.alt}
                        className="d-block w-100"
                        lazyLoad
                        sizes={image.sizes}
                        src={image.url}
                        srcSet={image.srcset}
                    />
                    {isSelected && (
                        <span
                            className={classNames(
                                'd-flex align-items-center justify-content-center position-absolute end-0 bottom-0',
                                styles.thumbnailCheck,
                            )}
                            aria-hidden="true"
                        >
                            <CheckCircleIcon size={24} className={styles.icon} />
                        </span>
                    )}
                </PureButton>
            ),
        };
    });

    return (
        <>
            <p className={classNames('mb-1x', styles.thumbnailLabel)}>
                {intl.formatMessage(messages.thumbnailLabel)}
            </p>
            <div ref={containerRef} className={styles.thumbnailSlider}>
                <LazySlider
                    a11yNextSlide={a11yNextSlide}
                    a11yPreviousSlide={a11yPreviousSlide}
                    config={thumbnailSliderConfig}
                    isFreeModeEnabled={false}
                    loop
                    onSlideChange={onSwiperSlideChange}
                    slides={slides}
                />
            </div>
        </>
    );
};

export { ThumbnailSlider };
